'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { Container } from './container';

interface HorizontalScrollbarProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  scrollContainerSelector?: string;
  scrollItemSelector?: string;
  showContainer?: boolean;
}

export function HorizontalScrollbar({
  children,
  className,
  scrollContainerSelector = '[data-scroll-items]',
  scrollItemSelector = '[data-scroll-item]',
  showContainer = true,
  ...props
}: HorizontalScrollbarProps) {
  const [scrollThumbPosition, setScrollThumbPosition] = useState(0);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(20);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(true);
  const itemsRef = useRef<HTMLDivElement>(null);

  const updateScrollState = useCallback(() => {
    if (!itemsRef.current) return;

    const container = itemsRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

    const thumbWidthPercent = Math.min((clientWidth / scrollWidth) * 100, 100);
    setScrollThumbWidth(thumbWidthPercent);

    const scrollPercent = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft : 0;

    const maxThumbPosition = 100 - thumbWidthPercent;

    setScrollThumbPosition(scrollPercent * maxThumbPosition);

    setShowPrev(scrollLeft > 0);
    setShowNext(scrollLeft < maxScrollLeft - 10);
  }, []);

  useEffect(() => {
    const itemsContainer = document.querySelector(scrollContainerSelector);
    if (itemsContainer) {
      itemsRef.current = itemsContainer as HTMLDivElement;
      itemsContainer.addEventListener('scroll', updateScrollState);

      setTimeout(updateScrollState, 100);

      window.addEventListener('resize', updateScrollState);

      setTimeout(updateScrollState, 500);
      setTimeout(updateScrollState, 1000);
    }

    return () => {
      if (itemsContainer) {
        itemsContainer.removeEventListener('scroll', updateScrollState);
      }
      window.removeEventListener('resize', updateScrollState);
    };
  }, [scrollContainerSelector, updateScrollState]);

  const handleScrollPrev = () => {
    if (!itemsRef.current) return;

    const container = itemsRef.current;
    const itemWidth =
      container.querySelector(scrollItemSelector)?.clientWidth || 0;
    container.scrollBy({ left: -itemWidth, behavior: 'smooth' });
  };

  const handleScrollNext = () => {
    if (!itemsRef.current) return;

    const container = itemsRef.current;
    const itemWidth =
      container.querySelector(scrollItemSelector)?.clientWidth || 0;
    container.scrollBy({ left: itemWidth, behavior: 'smooth' });
  };

  // biome-ignore lint/correctness/noUndeclaredVariables: <explanation>
  const handleThumbDrag = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!itemsRef.current) return;

    const container = itemsRef.current;
    const scrollTrack = e.currentTarget.parentElement;
    if (!scrollTrack) return;

    const startX = e.clientX;
    const startLeft = container.scrollLeft;
    const trackWidth = scrollTrack.clientWidth;
    const maxScroll = container.scrollWidth - container.clientWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const scrollRatio = deltaX / trackWidth;
      const newScrollLeft = startLeft + scrollRatio * maxScroll;
      container.scrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
      moveEvent.preventDefault();
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = 'none';
  };

  const ScrollbarContent = (
    <>
      <div className="relative">
        <div
          className="group/scrollbar absolute top-full flex h-4 w-full cursor-pointer touch-none items-center md:pr-11"
          data-scroll-bar=""
        >
          <div className="relative h-0.5 w-full bg-gray-200 transition-all ease-in-out group-hover/scrollbar:h-0.5">
            <button
              type="button"
              className="[&:active_span]:!h-0.5 absolute top-1/2 flex min-h-full cursor-grab items-center justify-center py-2 will-change-transform active:cursor-grabbing"
              data-scroll-bar-thumb=""
              aria-label="Scrollbar Thumb"
              style={{
                width: `${scrollThumbWidth}%`,
                left: `${scrollThumbPosition}%`,
                transform: 'translateY(-50%)',
              }}
              onMouseDown={handleThumbDrag}
            >
              <span className="block h-0.5 w-full bg-[var(--text-color)] transition-all ease-in-out group-hover/scrollbar:h-0.5" />
            </button>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="group -translate-y-1/2 absolute top-2 right-14 flex h-8 w-8 items-center justify-center rounded-full border border-[var(--text-color)] bg-background stroke-2 text-[var(--text-color)] shadow transition-all ease-in-out"
        style={{
          opacity: showPrev ? 1 : 0,
          pointerEvents: showPrev ? 'inherit' : 'none',
        }}
        data-scroll-prev=""
        onClick={handleScrollPrev}
      >
        <span className="sr-only">Show Previous Item</span>
        <svg
          className="mr-1 h-3 w-3 rotate-180 stroke-2"
          fill="none"
          viewBox="0 0 10 10"
          aria-hidden="true"
          stroke="currentColor"
        >
          <path
            className="opacity-0 transition group-hover:opacity-100"
            d="M0 5h7"
          />
          <path
            className="transition group-hover:translate-x-[3px]"
            d="M1 1l4 4-4 4"
          />
        </svg>
      </button>
      <button
        type="button"
        className="group -translate-y-1/2 absolute top-2 right-5 flex h-8 w-8 items-center justify-center rounded-full border border-[var(--text-color)] bg-background stroke-2 text-[var(--text-color)] shadow transition-all ease-in-out"
        style={{
          opacity: showNext ? 1 : 0,
          pointerEvents: showNext ? 'inherit' : 'none',
        }}
        data-scroll-next=""
        onClick={handleScrollNext}
      >
        <span className="sr-only">Show Next Item</span>
        <svg
          className="ml-1 h-3 w-3 stroke-2"
          fill="none"
          viewBox="0 0 10 10"
          aria-hidden="true"
          stroke="currentColor"
        >
          <path
            className="opacity-0 transition group-hover:opacity-100"
            d="M0 5h7"
          />
          <path
            className="transition group-hover:translate-x-[3px]"
            d="M1 1l4 4-4 4"
          />
        </svg>
      </button>
      {children}
    </>
  );

  if (showContainer) {
    return (
      <Container
        data-scroll-controls=""
        className={cn('relative h-fit w-full select-none pb-10', className)}
        {...props}
      >
        {ScrollbarContent}
      </Container>
    );
  }

  return (
    <div
      data-scroll-controls=""
      className={cn('relative h-fit w-full select-none pb-10', className)}
      {...props}
    >
      {ScrollbarContent}
    </div>
  );
}
